import type {
  DxfEntityKey,
  DxfEntityRoles,
  DxfMappingRole,
  DxfScene,
  DxfText,
} from '@frontend/api';
import type {
  Aabb2,
  ChunkGraph,
  Loop,
  LoopIndex,
  MarqueeMergeMode,
  Point2,
  ReadableStore,
  MarqueeState,
  Selection,
  SelectionMergeMode,
  SelectionRef,
  SelectionSummary,
  SnapCandidate,
  SnapIndex,
  ViewportSize,
  ViewportState,
} from '@frontend/viewer-core';

import type { HistoryStore } from './historyStore';
import type { MappingValidationManager } from './mappingValidation';
import type { PanelSelectionMode } from './selectionModifiers';

/**
 * Lifecycle status of the drawing fetch / derive pipeline.
 *
 * Prefer branching on `DxfSceneLoadState` (or `DxfSceneLoadInput`) rather than
 * reading this alone — status without its payload is how null checks proliferate.
 */
export type DxfSessionStatus = DxfSceneLoadState['status'];

/**
 * What React Query (or any fetch boundary) hands the session.
 *
 * Status and payload are tied: a `'ready'` input always carries a `scene`, an
 * `'error'` never does. The loader derives `DrawingModel` and publishes
 * `DxfSceneLoadState`.
 */
export type DxfSceneLoadInput =
  | { status: 'idle' }
  | { status: 'loading'; sceneUrl: string }
  | { status: 'ready'; sceneUrl: string; scene: DxfScene }
  | { status: 'error'; sceneUrl: string | null; error?: unknown };

/**
 * Session-facing load state: same discriminant as the input, with `model`
 * present only when `'ready'`.
 *
 * Gate React UI on `status`, then read `model` / `scene` without further null
 * checks — the same pattern as `ReqsReviewConfigContext`.
 */
export type DxfSceneLoadState =
  | { status: 'idle'; model: null }
  | { status: 'loading'; sceneUrl: string; model: null }
  | {
      status: 'ready';
      sceneUrl: string;
      scene: DxfScene;
      model: DrawingModel;
    }
  | {
      status: 'error';
      sceneUrl: string | null;
      model: null;
      error?: unknown;
    };

export const INITIAL_LOAD_STATE: DxfSceneLoadState = {
  status: 'idle',
  model: null,
};

/**
 * The session's **reactive tier**: discrete, user-initiated state. Prefer the
 * controller lenses on `DxfViewerSession` (`tools`, `overlays`, …); reach for
 * `useSessionState` only for ad-hoc cross-field selects.
 *
 * Load / model lifecycle lives on `session.load`, not here — so a drawing
 * arriving does not share a store with selection toggles.
 *
 * The rule that keeps the viewer from re-rendering the page: anything that changes
 * at frame rate — the viewport, the pointer, a live marquee — belongs on the live
 * tier (`session.viewport` and friends) and must never appear here.
 */
export type DxfSessionState = {
  /**
   * Hidden rather than visible, so a layer index the set has never heard of
   * defaults to visible and a newly-loaded drawing needs no seeding.
   */
  hiddenLayers: ReadonlySet<number>;
  /** Layer the user is hovering in the layers panel, or `null`. */
  hoveredLayer: number | null;
  /**
   * What is picked, in the order it was picked.
   *
   * On the reactive tier because it only changes when a person acts, and because
   * the details overlay and the layers panel both have to see it.
   */
  selection: Selection;
  /**
   * What clicking right now would pick, or empty.
   *
   * A list rather than one ref because a pick is not always one thing: loop-aware
   * chaining resolves a click into every edge the chain runs through, and the
   * hover accent has to promise exactly what the click will take. It holds more
   * than one entry whenever loop granularity picks a chain that a role boundary
   * has split, or that a junction resolves across more than one stitched loop.
   *
   * Frequent but discrete — it changes when the target under the cursor changes,
   * not as the cursor moves — so it belongs here rather than on the live tier.
   */
  hovered: readonly SelectionRef[];
  /** The one active tool. */
  tool: ToolId;
  /** Whether a click picks a whole loop or the single edge under it. */
  selectionGranularity: SelectionGranularity;
  /**
   * The committed ends of a measurement, in world units.
   *
   * Only the fixed points live here — the rubber-band end follows the cursor and
   * so belongs on the live tier with everything else that moves per frame.
   */
  measure: MeasureState;
  overlays: OverlayState;
  /**
   * Viewing, mapping, or viewing interrogation results.
   *
   * A separate axis from `tool`, not another `ToolId`, because the two differ in
   * exactly the way that matters: switching tools discards the selection, and
   * switching modes must not. The spec requires a selection made while viewing to
   * survive into mapping so it can be assigned a role immediately.
   */
  mode: ViewerMode;
  mapping: MappingState;
  /**
   * Role the user is hovering in the mapping panel, or `null`.
   *
   * The counterpart to `hoveredLayer`, and it drives the same hover accent — the
   * spec asks both panels to preview what a click would select.
   */
  hoveredRole: DxfMappingRole | null;
  /** True while another viewer is in front; input is detached and drawing stops. */
  isPaused: boolean;
  /**
   * Entities and gap spans called out while in `interrogation` mode, each with
   * the colour to draw it in. Emptied when the mode is left.
   *
   * Kept apart from `selection` because the canvas does not own it: a click on the
   * drawing must not clear it, and a mapping edit must never act on it.
   */
  highlights: Highlights;
};

/**
 * Every value a brush tool can take, one per mapping role.
 *
 * Built from `DxfMappingRole` rather than spelled out, so a role added to
 * `DXF_MAPPING_ROLE` gets a brush automatically instead of leaving a silent gap
 * here.
 */
export type BrushToolId = `brush-${DxfMappingRole}`;

/**
 * Exactly one of these is active.
 *
 * Loop vs. edge selection is deliberately *not* one of these values: it is an
 * orthogonal axis (`SelectionGranularity`), for the same reason `mode` is —
 * `ToolManager.set` clears the selection on every switch, and the spec requires a
 * selection to survive a change of selection mode. Keeping it off `ToolId` also
 * means arming a brush cannot silently discard the granularity the user chose.
 */
export type ToolId = 'select' | 'measure' | BrushToolId;

/**
 * What one click picks.
 *
 * `'loop'` takes the whole stitched chain the pointer landed on; `'edge'` takes
 * only the run of segments one entity contributed to it. Both granularities have
 * always existed in `SelectionRef` — double-click drilling produces an `edge` ref
 * even under loop selection — so this decides which one a *single* click yields,
 * not what the selection is able to hold.
 */
export type SelectionGranularity = 'loop' | 'edge';

/**
 * What the viewer is for right now.
 *
 * `view` carries measure, axis dimensions, optimal bounding box, and chat
 * annotation. `mapping` replaces that chrome with role assignment and nothing
 * else. `interrogation` is viewing interrogation results: the viewing tools
 * stay, the layer and mapping panels go, and `highlights` says which entities
 * the results call out. `mapping` and `interrogation` exclude each other.
 */
export type ViewerMode = 'view' | 'mapping' | 'interrogation';

/** CSS colour per highlighted entity — see `DxfSessionState.highlights`. */
export type EntityHighlights = ReadonlyMap<DxfEntityKey, string>;

/** A gap segment in drawing units, such as a clearance between two entities. */
export type HighlightSpan = {
  readonly segment: readonly [Point2, Point2];
  readonly color: string;
};

/** What `interrogation` mode calls out — see `DxfSessionState.highlights`. */
export type Highlights = {
  readonly entities: EntityHighlights;
  /** Drawn whatever layers are hidden: a span belongs to no layer. */
  readonly spans: readonly HighlightSpan[];
};

/**
 * Roles per source entity.
 *
 * A `Map` rather than the wire format's `Record`: the wire shape is JSON, but the
 * working copy is edited on every click over a drawing that may carry tens of
 * thousands of entities, and object spread on a `Record` that size is markedly
 * worse than a `Map` copy. Converted at the persistence boundary, not here.
 */
export type MappingAssignments = ReadonlyMap<DxfEntityKey, DxfEntityRoles>;

/** Entity keys per role — the inverse of `MappingAssignments`. */
export type RoleIndex = ReadonlyMap<DxfMappingRole, ReadonlySet<DxfEntityKey>>;

/**
 * One step on the mapping undo stack.
 *
 * Selection and roles together, because the spec puts both on the same stack and
 * an undo has to restore both at once — see `createMappingSnapshotSlice`.
 */
export type MappingSnapshot = {
  readonly selection: Selection;
  readonly draft: MappingAssignments | null;
};

/**
 * The mapping overlay: what is persisted, what is being edited, and whether
 * editing is allowed at all.
 *
 * `draft` is the ephemeral working copy the AC calls for — nothing reaches the
 * server until Save. `baseline` is what `draft` was when mapping mode was entered,
 * and it is the whole of the dirty check: `draft !== baseline` by reference. That
 * comparison is free (every edit builds a new `Map`) and it unwinds correctly —
 * undoing back to the entry state restores the identical reference, so the flag
 * goes false again rather than latching on.
 */
export type MappingState = {
  /** Last state persisted by the server, or `null` when no mapping row exists yet. */
  readonly saved: MappingAssignments | null;
  /** The working copy. `null` outside a mapping session. */
  readonly draft: MappingAssignments | null;
  /** `draft` at mapping-mode entry. Cancel restores this; the dirty check compares to it. */
  readonly baseline: MappingAssignments | null;
  /**
   * When set, mapping is read-only. Copy comes from the viewer shell — the session
   * does not interpret it.
   *
   * Mapping mode cannot be entered, and a session already in progress must fail
   * to save rather than silently discarding the user's work.
   */
  readonly lockReason: string | undefined;
};

export const INITIAL_MAPPING: MappingState = {
  saved: null,
  draft: null,
  baseline: null,
  lockReason: undefined,
};

/**
 * What one Measure click picked: a measure target.
 *
 * `spot` is always where the click landed, in drawing units, so any pair can
 * fall back to the straight distance between the two spots. `entityKey` is the
 * entity the target came from, or `null` for a free point. It is never a loop
 * or chunk id, because those change every time the drawing is re-derived.
 */
export type MeasureTarget =
  | {
      /** A corner, midpoint or centre in snap range, or a free point. */
      readonly kind: 'point';
      readonly spot: Point2;
      readonly entityKey: DxfEntityKey | null;
    }
  | {
      /**
       * A LINE entity, a polyline with exactly one straight segment, or a
       * segment the scene marks straight.
       */
      readonly kind: 'line';
      readonly spot: Point2;
      readonly entityKey: DxfEntityKey;
      /** The line's two ends. Perpendiculars go to its infinite extension. */
      readonly segment: readonly [Point2, Point2];
    }
  | {
      /**
       * A CIRCLE entity, or ARCs that close a circle between them. It measures
       * from its centre.
       */
      readonly kind: 'circle';
      readonly spot: Point2;
      readonly entityKey: DxfEntityKey;
      readonly center: Point2;
      readonly radius: number;
    }
  | {
      /**
       * A spot on any other curve: arcs, ellipses, splines, and polyline
       * segments not known to be straight. It measures as a point.
       */
      readonly kind: 'curve';
      readonly spot: Point2;
      readonly entityKey: DxfEntityKey;
    };

/**
 * Which rule produced a measurement — see `measureBetween`.
 *
 * `pointToPoint` is also the fallback when no caliper rule applies: the
 * straight distance between the two click spots. `orthoLocked` is
 * point-to-point with the second end held to the nearest 45°.
 */
export type MeasurementKind =
  | 'orthoLocked'
  | 'pointToPoint'
  | 'pointToCenter'
  | 'centerToLine'
  | 'centerToCenter'
  | 'parallelGap'
  | 'concentricGap'
  | 'sameTarget';

/**
 * A measurement between two targets, recording enough to be saved and replayed:
 * the rule, both targets with their click spots, and the result.
 */
export type Measurement = {
  readonly kind: MeasurementKind;
  readonly from: MeasureTarget;
  readonly to: MeasureTarget;
  /** The measured line's ends, in drawing units. `start` is on `from`'s side. */
  readonly start: Point2;
  readonly end: Point2;
  readonly distance: number;
};

export type MeasureState = {
  /** The first target, from the first click until the next one starts over. */
  readonly from: MeasureTarget | null;
  /** The fixed measurement, once the second click lands. */
  readonly measurement: Measurement | null;
};

export const EMPTY_MEASURE: MeasureState = { from: null, measurement: null };

/** The Measure tool's modifier keys, as held right now. */
export type MeasureModifiers = {
  /** Shift: holds the second end to the nearest 45° from the first point. */
  readonly isOrthoLocked: boolean;
  /**
   * Ctrl or ⌘: the click lands exactly where it is, with no snapping, no
   * target and no caliper rule.
   */
  readonly isFreePoint: boolean;
};

export const NO_MEASURE_MODIFIERS: MeasureModifiers = {
  isOrthoLocked: false,
  isFreePoint: false,
};

/**
 * What one stitched chunk, or one segment of it, is to the Measure tool.
 *
 * Decided from the scene row the chunk came from, never from the chunk's own
 * points: stitching can cut a longer polyline into two-point chunks, and those
 * must not be taken for lines. A row that names its straight segments is
 * decided segment by segment.
 */
export type MeasureEdge =
  | { readonly kind: 'line'; readonly segment: readonly [Point2, Point2] }
  | {
      readonly kind: 'circle';
      readonly center: Point2;
      readonly radius: number;
    }
  | { readonly kind: 'curve' };

/**
 * A snap point, with the line or circle a click on it picks: a line's midpoint
 * picks the line, and the centre of a CIRCLE, or of ARCs that close a circle,
 * picks the circle. Any other snap picks a point.
 */
export type MeasureSnapCandidate = SnapCandidate & {
  readonly measureEdge?: Exclude<MeasureEdge, { kind: 'curve' }>;
};

export type OverlayKey =
  | 'axisDimensions'
  | 'optimalBoundingBox'
  | 'pmi'
  | 'layerColoring'
  | 'mappingColoring';

export type OverlayState = Readonly<Record<OverlayKey, boolean>>;

/**
 * Layer colouring starts on, matching how a drawing looks in any CAD package.
 * Mapping colouring starts on too — the spec asks for roles to be visible by
 * default wherever a file has them, in viewing mode as well as while mapping — and
 * costs nothing on a file with no mapping, which paints no overlay at all. The
 * rest are opt-in: they are annotation, not the drawing.
 */
export const INITIAL_OVERLAYS: OverlayState = {
  axisDimensions: false,
  optimalBoundingBox: false,
  pmi: false,
  layerColoring: true,
  mappingColoring: true,
};

/**
 * Where the pointer is, and what it would snap to.
 *
 * **Live tier.** This changes with every pointer move, so nothing in React may
 * subscribe to it — the annotation overlay reads it from a frame callback.
 */
export type PointerState = {
  /** Container-relative screen position, or `null` when the pointer has left. */
  screen: Point2 | null;
  /** World position, already moved onto the snap target when there is one. */
  world: Point2 | null;
  /**
   * The point to draw the snap ring at. `null` while a line or circle is lit,
   * unless it was picked by its midpoint or centre.
   */
  snap: SnapCandidate | null;
  /** What a Measure click here would pick. `null` under any other tool. */
  target: MeasureTarget | null;
};

export const EMPTY_POINTER: PointerState = {
  screen: null,
  world: null,
  snap: null,
  target: null,
};

/**
 * A pick that found nothing — what `pickAt` returns over blank canvas, and what
 * `hovered` holds when the pointer is over nothing.
 *
 * Interned, for the same reason `EMPTY_SELECTION` is: the accent overlay decides
 * whether to rebuild by comparing `hovered` by reference, so "nothing" has to be
 * one value rather than an endless supply of distinct empty arrays.
 */
export const NO_PICK: readonly SelectionRef[] = [];

export const NO_HIGHLIGHTS: Highlights = { entities: new Map(), spans: [] };

export const INITIAL_SESSION_STATE: DxfSessionState = {
  hiddenLayers: new Set(),
  hoveredLayer: null,
  selection: new Map(),
  hovered: NO_PICK,
  tool: 'select',
  // Loop is the default granularity: the one most picks want.
  selectionGranularity: 'loop',
  measure: EMPTY_MEASURE,
  overlays: INITIAL_OVERLAYS,
  mode: 'view',
  mapping: INITIAL_MAPPING,
  hoveredRole: null,
  isPaused: false,
  highlights: NO_HIGHLIGHTS,
};

/**
 * A scene text stamped with an id unique within one `deriveModel` pass.
 *
 * Ids are derived (not wire) and must not be persisted — see `textKey`.
 */
export type ModelText = {
  id: string;
  text: DxfText;
};

/**
 * Where one entity's geometry ended up after stitching: a chunk within a loop.
 *
 * The address is positional within a derivation — like `Loop.id` itself — so it
 * must never be persisted. Mapping persists `DxfEntityKey`; this is only how a
 * key is turned back into geometry to paint.
 */
export type ChunkAddress = {
  readonly loopId: number;
  readonly chunkIndex: number;
};

/**
 * A drawing reduced to what the viewer asks of it, derived once per scene.
 *
 * This is the substrate every selection rule stands on: picking, marquee
 * enclosure and crossing, per-layer selection, path length and enclosed area all
 * read from here rather than from the wire payload.
 */
export type DrawingModel = {
  scene: DxfScene;
  /**
   * Scene texts with ids unique within this derivation.
   *
   * Parallel to `scene.texts` and in the same order, so the text overlay can keep
   * addressing spans by child index. Ids are for React keys and future addressing;
   * they are regenerated on every re-derivation.
   */
  texts: readonly ModelText[];
  /** World bounds of all geometry, or `null` for an empty drawing. */
  bounds: Aabb2 | null;
  /** World bounds per layer index, for visibility-aware fitting. */
  boundsByLayer: ReadonlyMap<number, Aabb2>;
  /**
   * Distinct *source entities* per layer index.
   *
   * Counted over `[h, ...src]` rather than over `scene.entities` rows: the backend
   * ships flattened primitives, so one DIMENSION arrives as a dozen rows sharing a
   * handle and counting rows overstates a drawing's contents badly.
   */
  entityCountsByLayer: ReadonlyMap<number, number>;
  /** Layer indices carrying reconstructed dimension annotation. */
  layersWithPmi: ReadonlySet<number>;
  /** Source-entity keys whose geometry is dimension annotation. */
  pmiEntityKeys: ReadonlySet<string>;
  /** True when the drawing carries no PMI at all, so the toggle can say so. */
  hasPmi: boolean;
  /**
   * Entity geometry stitched into chains. Ids are positional within this
   * derivation and must not be persisted — see `Loop`.
   */
  loops: readonly Loop[];
  /**
   * `loops` keyed by id.
   *
   * Built once here rather than rebuilt per call, as `selectionPositions` and
   * `summarizeSelection` do: mapping resolves loops on every role assignment and
   * on every rebuild of the role overlay, which is far too hot to pay an O(loops)
   * scan for. Ids happen to equal array indices today, but the rest of the stack
   * deliberately does not rely on that, and neither does this.
   */
  loopsById: ReadonlyMap<number, Loop>;
  /** Spatial index over those loops, for picking and marquee queries. */
  index: LoopIndex;
  /**
   * The stitcher's welded endpoint graph, kept so chains can be recomputed
   * against the *current* mapping without re-deriving the model.
   *
   * Pure geometry: it says which chunks meet at which welded node and nothing
   * about why any two of them should chain. Loop-granularity picking walks it
   * with a role-aware key — see `chainKeyOf` — which is what lets a chain stop
   * at a role boundary and run through a junction the stitcher had to split at,
   * while every `loopId` stays exactly where it was.
   */
  chunkGraph: ChunkGraph;
  /** Endpoints, midpoints and curve centres, for the measure tool. */
  snapIndex: SnapIndex<MeasureSnapCandidate>;
  /**
   * What one segment of a chunk is to the Measure tool. `null` for dimension
   * and annotation geometry, which is never a target.
   */
  measureEdgeAt(
    loopId: number,
    chunkIndex: number,
    segmentIndex: number,
  ): MeasureEdge | null;
  /**
   * How close two points must be to count as the same point, in drawing units.
   * Scaled to the drawing, so it means the same in millimetres and in inches.
   */
  samePointTolerance: number;

  /* --- Mapping substrate -------------------------------------------------
   * Mapping is keyed by `DxfEntityKey`, which is stable and persisted, while
   * selection is keyed by loop/chunk, which is positional and must not be. These
   * four fields are the bridge between the two vocabularies.
   * ---------------------------------------------------------------------- */

  /**
   * Every distinct source-entity key in the drawing, in scene order (geometry
   * first, then text).
   *
   * The universe a mapping has to cover: "if a mapping is present, everything is
   * mapped" is the assertion `assignments.size === entityKeys.length`.
   */
  entityKeys: readonly DxfEntityKey[];
  /**
   * Source entity types per key.
   *
   * A set rather than a single value because one key can cover many primitives —
   * an INSERT explodes into rows sharing `[h, ...src]`. The bending rule is
   * therefore "every primitive of this key is a LINE", not "the first one is".
   *
   * Text rows contribute `MTEXT` when they wrap and `TEXT` otherwise.
   */
  entityTypesByKey: ReadonlyMap<DxfEntityKey, ReadonlySet<string>>;
  /**
   * Where each entity's geometry landed after stitching.
   *
   * Many addresses per key, not one: the rows sharing a key are stitched
   * independently, so a single entity can own chunks in several different loops.
   * Assigning a role to a selected loop therefore reaches geometry outside that
   * loop, and painting a role has to gather all of it.
   */
  chunksByEntityKey: ReadonlyMap<DxfEntityKey, readonly ChunkAddress[]>;
  /**
   * Entity keys that produced no stitched geometry — fills (hatch), single-point
   * POINTs, and text.
   *
   * They still hold a role, so the "everything is mapped" invariant holds, but
   * they can never be picked on the canvas and never appear in a role's painted
   * geometry.
   */
  unstitchedEntityKeys: ReadonlySet<DxfEntityKey>;
  /**
   * Entity keys belonging to `scene.texts`.
   *
   * With `pmiEntityKeys`, this is the "GD&T and callouts" set that seeds to
   * `ignore` rather than to `cutting`.
   */
  textEntityKeys: ReadonlySet<DxfEntityKey>;
};

export type ViewportManager = ReadableStore<ViewportState> & {
  /** Live measured container size. Written by the session's ResizeObserver. */
  readonly size: ReadableStore<ViewportSize>;

  set(next: ViewportState): void;
  panBy(deltaX: number, deltaY: number): void;
  /** `factor` > 1 zooms in, about a container-relative screen point. */
  zoomAt(screenPoint: Point2, factor: number): void;
  /** `1` clockwise, `-1` counter-clockwise, about the drawing's centre. */
  rotate(delta: 1 | -1): void;
  /**
   * Frames the drawing with padding, keeping the current rotation.
   * `visibleOnly` excludes hidden layers, falling back to the full extents when
   * every layer is hidden so a fit always frames something.
   */
  fit(options?: { visibleOnly?: boolean }): void;
  /** Returns rotation to zero and re-frames. */
  reset(): void;

  worldToScreen(world: Point2): Point2;
  screenToWorld(screen: Point2): Point2;
};

export type LayerManager = {
  isVisible(layerIndex: number): boolean;
  setVisible(layerIndex: number, isVisible: boolean): void;
  setAllVisible(isVisible: boolean): void;
  /** Highlights every loop drawing on the layer, until set back to `null`. */
  setHovered(layerIndex: number | null): void;
  /**
   * Selects every loop drawing on the layer.
   *
   * Takes a merge mode rather than an additive flag, so a Shift-click on a
   * layer that is already selected can take it back out — see
   * `panelRowMergeMode`, which is where a row decides which mode it meant.
   * Never `'toggle'`: see `PanelSelectionMode`.
   */
  select(layerIndex: number, mode?: PanelSelectionMode): void;
  /** Frames the layer's geometry. */
  frame(layerIndex: number): void;
};

export type SelectionManager = ReadableStore<Selection> & {
  /** Replaces the selection outright. */
  set(refs: readonly SelectionRef[]): void;
  /**
   * Applies `refs` under `mode` — the one write every selecting gesture makes.
   *
   * A single entry point rather than an `add`/`remove`/`toggle` trio, because
   * the caller's job is to decide what its modifiers meant and the manager's job
   * is to apply that decision. `'replace'` routes through `set`, so the
   * drilled-edge bookkeeping cannot be skipped by picking the wrong method.
   */
  merge(refs: readonly SelectionRef[], mode: SelectionMergeMode): void;
  clear(): void;
  setHovered(refs: readonly SelectionRef[]): void;
  /**
   * What clicking a container-relative screen point would pick, or empty.
   *
   * Returns a list, not one ref, because a pick is not always one thing. Under
   * loop granularity a click resolves to every edge the role-aware chain runs
   * through, and the caller must not have to know whether it got one ref or
   * twelve — the click handler, the hover accent and the brush all treat the
   * answer as "the things this click acts on".
   *
   * Which granularity applies is the controller's own business: callers ask what
   * a click would do, not what mode the viewer is in.
   *
   * Tolerance is a screen distance converted through the current zoom, so picking
   * feels the same however far in the user is.
   */
  pickAt(screenPoint: Point2): readonly SelectionRef[];
  /**
   * Alternates between the edge under the pointer and its parent loop.
   *
   * The double-click gesture. Selecting is whole-loop by default, so this is the
   * way into the individual run one entity contributed.
   *
   * `'replace'` (plain double-click) is the drill-in / drill-out toggle and
   * replaces the selection. Any other mode — Shift's `'toggle'` — keeps the
   * rest of the selection and merges that one edge, and does not drill out:
   * coming back up to the parent loop would dump a whole chain into a
   * selection the user was extending.
   */
  drillAt(screenPoint: Point2, mode?: SelectionMergeMode): void;
  /** Measures the current selection, for the details overlay. */
  summarize(): SelectionSummary;
};

export type MeasureManager = ReadableStore<MeasureState> & {
  /** Picks the target under the pointer. The second one fixes the measurement. */
  commitPoint(screenPoint: Point2): void;
  /**
   * What clicking `target` now would measure, or `null` when it would not
   * finish a measurement: before the first click, or once one is fixed.
   */
  preview(target: MeasureTarget | null): Measurement | null;
  clear(): void;
  /** Live tier: Shift and Ctrl/⌘, which the input controller keeps in sync. */
  readonly modifiers: ReadableStore<MeasureModifiers>;
  setModifiers(next: MeasureModifiers): void;
};

/**
 * Which mapping role UI is on screen: the selection flyout, the right-click
 * menu, or neither.
 *
 * Exclusive by construction — a tagged union rather than two independent
 * booleans — so the flyout and the context menu never have to name each other.
 * Hidden is also the derived result of an empty selection or of leaving
 * mapping mode, which is why both UIs vanish when the selection clears
 * without either component watching the other.
 *
 * `hidden` while a selection still exists is the pointerdown/pointerup gap:
 * dismissing the context menu must not fall through to the flyout for a frame
 * before canvas selection updates on release. That hold is an internal of the
 * chrome controller, not a flag one menu sets to suppress the other.
 */
export type RoleChromeState =
  | { readonly kind: 'hidden' }
  | { readonly kind: 'flyout' }
  | { readonly kind: 'context'; readonly anchor: Point2 };

export type RoleChromeManager = ReadableStore<RoleChromeState> & {
  /** Opens the right-click menu at a container-relative screen point. */
  open(screenPoint: Point2): void;
  /**
   * Hides chrome without letting the flyout take the slot until `release`.
   * The dismiss pointerdown of a context menu.
   */
  dismiss(): void;
  /** Hides chrome and allows the flyout to return if a selection remains. */
  close(): void;
  /** Ends a `dismiss` hold. */
  release(): void;
};

export type ToolManager = ReadableStore<ToolId> & {
  /**
   * Switches tools. Clears the selection and any measurement, because a tool
   * change is a change of intent — the spec is explicit that neither survives it.
   */
  set(tool: ToolId): void;
  /**
   * Arms `role`'s brush. Idempotent when that brush is already active — use
   * `set('select')` (Esc / Choose) to put a brush down.
   *
   * The one entry point for both a brush button and its keyboard shortcut, so
   * arming from the keyboard behaves exactly like clicking the button — the
   * spec is explicit the two must match.
   */
  armBrush(role: DxfMappingRole): void;
};

export type SelectionGranularityManager =
  ReadableStore<SelectionGranularity> & {
    /**
     * Switches what a click picks.
     *
     * Deliberately keeps the selection, unlike `ToolManager.set` — the spec
     * requires a selection to survive a change of selection mode, so this is a
     * plain write and nothing else.
     */
    set(granularity: SelectionGranularity): void;
    /**
     * The granularity in force right now — the stored one, inverted while the
     * Alt modifier is held.
     *
     * What every gesture reads. `getState` is for the toolbar, which lights the
     * mode the user chose rather than the one a held key is borrowing.
     */
    effective(): SelectionGranularity;
    /** Presses or releases the invert modifier (Alt). */
    setInverted(isInverted: boolean): void;
    isInverted(): boolean;
  };

export type ModeManager = ReadableStore<ViewerMode> & {
  /**
   * Switches modes.
   *
   * Unlike `ToolManager.set` this deliberately keeps the selection — see
   * `DxfSessionState.mode`. Entering mapping begins a mapping session; leaving
   * ends one. Refused when the file is locked. `mapping` and `interrogation`
   * exclude each other: neither can be entered from the other. Leaving
   * `interrogation` clears the highlights.
   */
  set(mode: ViewerMode): void;
  /** False when the file is locked, no drawing has loaded, or results are being viewed. */
  canEnterMapping(): boolean;
};

/** What `interrogation` mode calls out — see `DxfSessionState.highlights`. */
export type HighlightManager = ReadableStore<Highlights> & {
  /** Replaces the highlighted entities and spans and their colours. */
  set(highlights: Highlights): void;
  clear(): void;
};

export type MappingManager = ReadableStore<MappingState> & {
  /** From the fetch boundary. Replaces `saved`; never disturbs an open session. */
  loadSaved(assignments: MappingAssignments | null): void;
  setLockReason(lockReason: string | undefined): void;

  /**
   * Seeds `draft` and `baseline` for a mapping session.
   *
   * From `saved` when the file has a mapping, and from defaults when it does not
   * — every entity cut except annotation, per the spec. Both fields get the *same*
   * reference, which is what makes "entering mapping mode is not an edit" true.
   */
  beginSession(): void;
  /** Drops `draft` and `baseline`. The mapping is either saved by now, or discarded. */
  endSession(): void;

  /** Applies `role` to specific entities. No-ops when nothing would change. */
  assign(keys: Iterable<DxfEntityKey>, role: DxfMappingRole): void;
  /**
   * Applies `role` to specific entities and drops the selection, in one write.
   *
   * What brush paint does: the painted geometry gets the role and nothing stays
   * selected, so the flyout does not linger over a previous pick.
   */
  assignAndClear(keys: Iterable<DxfEntityKey>, role: DxfMappingRole): void;
  /** Applies `role` to whatever is selected, resolved through `selectionEntityKeys`. */
  assignToSelection(role: DxfMappingRole): void;
  /**
   * Toggles `role` on the selection, keeping the selection.
   *
   * Etch and bend use this so a second pick can land on the same edge — clearing
   * after the first would leave the next click selecting the parent loop.
   */
  toggleRoleOnSelection(role: DxfMappingRole): void;
  /**
   * Toggles `role` on the selection and drops the selection, in one write.
   *
   * When every selected entity already carries `role`, it is removed (unless that
   * would leave the entity with no roles). Otherwise the role is applied. Exclusive
   * roles (cut, ignore) use this so the flyout closes after a pick.
   */
  toggleRoleOnSelectionAndClear(role: DxfMappingRole): void;
  /** Discards edits, restoring `baseline`. */
  revert(): void;
  /**
   * Accepts a saved mapping as the new truth: `saved` and `baseline` both become
   * it, so the session reads as clean without the draft being torn down.
   *
   * Called with the server's response rather than with the draft, so what the
   * session believes is saved is what the server actually stored.
   */
  commitSaved(assignments: MappingAssignments): void;

  /**
   * Selects every entity in a role.
   *
   * Same modifier handling as the layers panel — the spec has clicking a mapping
   * respond exactly the way clicking a layer does, which is why both rows read
   * their mode from the one `panelRowMergeMode`. Never `'toggle'`: see
   * `PanelSelectionMode`.
   */
  selectRole(role: DxfMappingRole, mode?: PanelSelectionMode): void;
  /** Previews what `selectRole` would take. `null` on leave. */
  setHoveredRole(role: DxfMappingRole | null): void;

  /**
   * What should be painted: the working copy when there is one, else what is
   * saved, else `null`. Roles are visible while viewing, not only while mapping.
   */
  effective(): MappingAssignments | null;
  /** True when this session has edits. `draft !== baseline`, by reference. */
  isDirty(): boolean;
  /** Entity keys per role, memoised against the assignments it was built from. */
  roleIndex(): RoleIndex;
  /** Entity count per role, for the mapping panel's badges. */
  counts(): ReadonlyMap<DxfMappingRole, number>;
  /**
   * Every role all selected entities share — including co-applied pairs.
   *
   * A store, not a method: the value is a function of the draft *and* the
   * selection, so React has to subscribe to both without naming either. Drives
   * checkmarks in the role flyout and the right-click menu.
   */
  readonly selectionSharedRoles: ReadableStore<ReadonlySet<DxfMappingRole>>;
};

/**
 * Overlay flags plus the mutations that flip them.
 *
 * Readable like `viewport`: consumers that only draw (tool overlays, the
 * renderer binding) subscribe to the store; the toolbar calls `set`/`toggle`.
 */
export type OverlayManager = ReadableStore<OverlayState> & {
  set(key: OverlayKey, isOn: boolean): void;
  toggle(key: OverlayKey): void;
};

/**
 * Drives a marquee drag.
 *
 * The gesture's rules live in `viewer-core`'s pure reducer; this is the part that
 * knows about the drawing — converting the box to world space and turning it into
 * a selection edit.
 */
/**
 * What a live marquee would do if it were released now.
 *
 * Refs rather than loop ids, because what a box catches depends on the selection
 * mode: under edge granularity it collects individual chunks, and under loop
 * granularity whole role-aware chains — which may be part of a stitched loop, or
 * span several. Sorted by key, so an unchanged catch compares equal cheaply and
 * the accent geometry is left alone while the box moves within the same set.
 */
export type MarqueePreview = {
  readonly refs: readonly SelectionRef[];
  readonly mode: MarqueeMergeMode;
};

export type MarqueeController = {
  begin(
    screenPoint: Point2,
    modifiers?: { isToggleModifierHeld?: boolean },
  ): void;
  move(screenPoint: Point2): void;
  setTranslateModifier(isTranslateModifierHeld: boolean): void;
  /** Escape: the box vanishes and the release changes nothing. */
  abort(): void;
  /**
   * Recomputes the live catch without moving the box — Alt inverts granularity
   * while the pointer is still, and the preview has to follow.
   */
  refresh(): void;
  /** Applies what the box caught and ends the gesture. */
  commit(): void;
  isActive(): boolean;
};

/**
 * What the session needs of a renderer, declared structurally rather than imported.
 *
 * `@frontend/dxf-renderer` is lint-banned from `src/**` because a static import —
 * even a type-only one, which lint cannot distinguish — is how three ends up in the
 * initial bundle. Naming the shape here keeps the session honest about its
 * dependency without creating one. `createDxfRenderer`'s return type satisfies it.
 */
export type DxfRenderTarget = {
  setScene(scene: DxfScene | null, pmiEntityKeys?: ReadonlySet<string>): void;
  setView(viewport: ViewportState, size: ViewportSize): void;
  setHiddenLayers(hidden: ReadonlySet<number>): void;
  setAccents(accents: AccentSpec | null): void;
  /**
   * The semantic overlay — one batch per mapping role — painted under the
   * interaction accents.
   *
   * Separate from `setAccents` because the two change on completely different
   * clocks: accents are rebuilt whenever the pointer crosses a new contour, while
   * roles only move when someone assigns one. Sharing a call would re-flatten every
   * role's geometry on every mouse move.
   */
  setOverlayBatches(batches: readonly AccentBatch[] | null): void;
  /**
   * Replaces the material-fill overlay: a translucent solid-sheet-vs-cutout
   * hint from even-odd parity over closed cutting contours —
   * see `buildMaterialFillRings`. `null` clears it: no eligible contour, or a
   * pair of them properly cross and the enclosed region is undefined.
   */
  setMaterialFill(rings: readonly MaterialFillRing[] | null): void;
  /** PMI geometry is shown independently of layer visibility. */
  setPmiVisible(isVisible: boolean): void;
  /** `false` draws the whole drawing in one neutral color. */
  setLayerColoring(isOn: boolean): void;
  /**
   * Hides the drawing's own line strokes outright when `false`, so a role's
   * dash does not show the original line through its gaps. Fills are
   * untouched — `setLayerColoring` still colours those.
   */
  setStrokesVisible(isVisible: boolean): void;
  /** The one colour the drawing takes while layer colouring is off. */
  setMonochromeColor(color: string): void;
  invalidate(): void;
};

/**
 * One region the material-fill overlay paints solid, with the holes cut into
 * it — see `buildMaterialFillRings` in `./materialFill`. Both rings are
 * already the "no closing duplicate point" form `Shape`/`Path` want.
 */
export type MaterialFillRing = {
  readonly outer: readonly Point2[];
  readonly holes: readonly (readonly Point2[])[];
};

/** Mirror of the renderer's accent types; see `DxfRenderTarget` for why. */
export type AccentBatch = {
  /**
   * Flat segment endpoint pairs, for a batch drawn as disconnected segments.
   * Unused (and empty) for a dashed batch — see `dash` and `polylines`.
   */
  readonly positions: readonly number[];
  readonly color: string;
  readonly widthPx: number;
  /**
   * `[onLength, offLength]` at 1 screen pixel per world unit, or `null`/absent
   * for a solid stroke. The role overlay and highlighted gap spans dash; hover,
   * selection and marquee preview are always solid.
   */
  readonly dash?: readonly [number, number] | null;
  /**
   * One continuous point list per chunk — see `entityKeyPolylines`. Required
   * when `dash` is set: a dashed batch draws each of these as its own joined
   * `Line2` rather than from `positions`, so a role's dash phase runs
   * continuously along each chunk's own curvature and never leaks into an
   * unrelated chunk concatenated into the same role. Ignored for a solid
   * stroke.
   */
  readonly polylines?: readonly (readonly number[])[];
};

export type AccentSpec = {
  /** Highlighted entities, one batch per colour. Painted first, under the rest. */
  readonly highlights: readonly AccentBatch[];
  /** The current hover state */
  readonly hover: AccentBatch | null;
  /** The current selection */
  readonly selection: AccentBatch | null;
  /** A temporary highlight during marquee dragging that shows What a live marquee would select if it were released now. */
  readonly preview: AccentBatch | null;
};

export type DxfViewerSession = {
  /** Reactive tier. Prefer controller lenses; use this for ad-hoc cross-field reads. */
  readonly state: ReadableStore<DxfSessionState>;
  /**
   * Drawing fetch / derive lifecycle. Gate UI on `status`, then read `model`
   * without further null checks when `'ready'`.
   */
  readonly load: ReadableStore<DxfSceneLoadState>;
  /** Narrow view of `load.status`. Prefer `load` when you also need the payload. */
  readonly status: ReadableStore<DxfSessionStatus>;
  /** Live tier — frame rate. The renderer and text layer subscribe; React does not. */
  readonly viewport: ViewportManager;
  /**
   * Derived drawing model, or `null` when not `'ready'`.
   *
   * Prefer `session.load` in React so a `'ready'` branch narrows to a non-null
   * model. Imperative controllers may keep using this getter.
   */
  readonly model: ReadableStore<DrawingModel | null>;
  readonly layers: LayerManager;
  readonly selection: SelectionManager;
  readonly tools: ToolManager;
  /** Whether a click picks a whole loop or one edge. Orthogonal to `tools`. */
  readonly selectionGranularity: SelectionGranularityManager;
  readonly mode: ModeManager;
  readonly mapping: MappingManager;
  /**
   * Undo/redo over selection *and* role assignment together.
   *
   * Only meaningful in mapping mode — the keyboard bindings are gated on it, and
   * the stack is reset on every entry and exit — but it lives on the session
   * rather than inside the mapping controller because the input surface drives it
   * and the toolbar renders from it.
   */
  readonly mappingHistory: HistoryStore;
  readonly measure: MeasureManager;
  readonly overlays: OverlayManager;
  readonly highlights: HighlightManager;
  readonly roleChrome: RoleChromeManager;
  /**
   * Narrow view of "this session holds unsaved mapping edits".
   *
   * A boolean rather than the mapping state itself so the page shell — which owns
   * the unsaved-work `Prompt` and knows nothing about DXFs — can subscribe without
   * naming a mapping type. Same treatment `hiddenLayers` gets for the renderer.
   */
  readonly hasUnsavedMapping: ReadableStore<boolean>;
  readonly marqueeController: MarqueeController;
  /** Live tier: the in-flight marquee, or `null`. */
  readonly marquee: ReadableStore<MarqueeState | null>;
  /** Live tier: what that marquee would collect, or `null`. */
  readonly marqueePreview: ReadableStore<MarqueePreview | null>;
  /** Live tier: pointer position and snap target. */
  readonly pointer: ReadableStore<PointerState>;
  /**
   * Narrow view of `state.hiddenLayers`, for consumers that must not depend on the
   * whole session — the renderer takes this rather than the session itself, which
   * is what keeps `@frontend/dxf-renderer` from naming anything in the app shell.
   */
  readonly hiddenLayers: ReadableStore<ReadonlySet<number>>;
  /**
   * The hidden layers as drawn — `hiddenLayers`, except none while viewing
   * interrogation results. What draws or picks geometry reads this; the layer
   * panel reads `hiddenLayers`.
   */
  readonly drawnHiddenLayers: ReadableStore<ReadonlySet<number>>;
  /**
   * The display toggles as drawn — `overlays`, except layer and mapping colouring
   * off while viewing interrogation results. What draws reads this; the toolbar
   * and panels read `overlays`.
   */
  readonly drawnOverlays: ReadableStore<OverlayState>;
  /** Narrow view of `state.hoveredLayer` (layers panel hover). */
  readonly hoveredLayer: ReadableStore<number | null>;
  /**
   * Part file the mounted `DXFViewer` is editing — the same uuid passed to the
   * mapping fetch, so Save targets the drawing on screen rather than whichever
   * file Redux happens to call active.
   */
  readonly partFileUuid: ReadableStore<string | null>;
  setPartFileUuid(partFileUuid: string | null): void;

  /**
   * Live mapping-validation state — see `MappingValidationManager`.
   */
  readonly mappingValidation: MappingValidationManager;

  /** Binds the ResizeObserver and input listeners. Returns a detach function. */
  attachSurface(host: HTMLElement): () => void;

  /**
   * Wires session state into a renderer and keeps it in sync. Returns a detach
   * function. Called once the lazy chunk resolves, so the shell is interactive
   * before the engine arrives.
   */
  attachRenderer(target: DxfRenderTarget): () => void;

  /**
   * Applies a load-state input from the fetch boundary and re-frames when the
   * drawing identity (`sceneUrl`) changes, or when the same URL delivers a new
   * scene derivation.
   */
  loadScene(input: DxfSceneLoadInput): void;
  setPaused(isPaused: boolean): void;

  /**
   * Centers on these entities' stitched geometry, plus any gap spans, and zooms
   * in to frame it.
   *
   * Center and scale lerp there. A move that crosses the view takes
   * `FEATURE_SNAP_DURATION_MS`; a shorter one takes less, and never under
   * `FEATURE_SNAP_MIN_DURATION_MS`. Zoom stops at
   * `FEATURE_SNAP_MAX_ZOOM` times a fit of the whole drawing, so a small
   * feature comes forward without filling the screen. No-op when nothing
   * resolves to bounds.
   */
  frameEntities(
    keys: readonly DxfEntityKey[],
    spans?: readonly (readonly [Point2, Point2])[],
  ): void;

  dispose(): void;
};
